/**
 * An audio engine that sounds a note on a recording of the instrument that plays it.
 *
 * The editor's own voice is an oscillator, which is enough to check that a score is right
 * and is nothing like enough to hear whether an arrangement is good: a flute, a horn and a
 * cello playing the same triangle wave differ only in pitch, and the whole question this
 * board asks is what a passage sounds like in somebody else's hands. So the board wraps the
 * browser's `AudioContext` in this, which answers {@link AudioEngine} exactly as the context
 * does and adds the one optional call the editor asks a richer engine for.
 *
 * The recordings are VSCO-2 Community Edition's, filled out with FluidR3_GM's where it
 * recorded nothing, fetched by `pnpm sounds:fetch` into `public/soundfonts/` and read by
 * `sampler-load.ts`, which also says which recording a note is played on: the nearest,
 * shifted at most three semitones, made at the dynamic nearer the note's, and for a string
 * under a `pizz.` one of the recordings made of it plucked; a mute and a bow at the bridge
 * were not recorded and are a colour on the recording there is (`sampler-manner.ts`). They are not in
 * the repository, and a clone that has not fetched them still plays -- every call here
 * answers `null` for a sound it does not have, and the editor sounds that note on its
 * oscillator. That is the only fallback there is, and it is silent by design: a board that
 * refused to play without 50 MB of samples would be worse than one that plays plainly.
 *
 * A recording is not a performance, so each is made ready once, as it is decoded
 * (`sampler-prepare.ts`: the silence before it cut, a loop written into its held tone so a
 * note can be held past the end of the recording), and each note is played the way its instrument is
 * (`sampler-seats.ts`: where it sits, how it lets go, how much darker it is played softly) in
 * a hall with a level the speakers can take (`sampler-room.ts`).
 *
 * Attribution is required wherever this sound is published: see
 * `public/soundfonts/CREDITS.txt`, which the fetch script writes beside the samples.
 */

import type {
  AudioEngine,
  AudioPipe,
  GainLike,
  SoundingTone,
  ToneLike,
  VoiceRequest,
} from "@app/score-editor";
import { hertz, swellInto } from "@app/score-editor";
import { choose, readRecorded, recordingsOf } from "./sampler-load";
import type { Recorded, Recording, Sound } from "./sampler-load";
import { colouredBy } from "./sampler-manner";
import { Room } from "./sampler-room";
import { brightness, TAIL_SPANS } from "./sampler-seats";

/**
 * What a recording is multiplied by so that loudness means one thing.
 *
 * The editor asks for a note at a `peak` between 0 and 1 and gets an oscillator of that
 * amplitude. FluidR3's recordings are mastered far below full scale -- measured across all
 * 1,320 of them, the median peaks at 0.107 and the loudest at 0.144 -- so playing them at
 * the asked-for gain would put the orchestra about nineteen decibels under the metronome
 * that is supposed to be underneath it. Nine is 1/0.107 to two figures: it makes `peak`
 * mean the same thing whichever voice answers, which is the contract `VoiceRequest` states.
 * VSCO-2's recordings are levelled to FluidR3's as they are fetched, so the one number holds
 * for both.
 *
 * The loudest samples land above 1 at full velocity, and that is what the room's trim and
 * limiter are for. Choosing the number by measurement rather than by ear is the point: a level
 * picked by listening is a judgement, and this one only has to match a level already set.
 */
const LEVEL = 9;

/** A browser `AudioContext` that sounds notes on recordings where it has them. */
export class SampledEngine implements AudioEngine {
  private readonly room: Room;

  constructor(
    private readonly context: BaseAudioContext,
    /** Each file's recordings, by its name, once they are ready. */
    private readonly samples = new Map<string, Recorded>(),
    /** Files already being fetched, so that two scores asking at once fetch once. */
    private readonly fetching = new Map<string, Promise<void>>(),
  ) {
    this.room = new Room(context);
  }

  get currentTime(): number {
    return this.context.currentTime;
  }

  /**
   * The room's dry input, not the speakers, so the oscillator voice is levelled and limited
   * with the samples. Dry and in the middle: the metronome has to be heard exactly where the
   * beat is, and a note outside every recording's range is sounded plainly so it stands out.
   */
  get destination(): AudioPipe {
    return this.room.dry;
  }

  createGain(): GainLike {
    return this.context.createGain();
  }

  createOscillator(): ToneLike {
    return this.context.createOscillator();
  }

  /** Samples a second of sound is made of, which a context writing to a file takes over. */
  get sampleRate(): number {
    return this.context.sampleRate;
  }

  resume(): Promise<void> {
    // An offline context runs when it is rendered, and refuses to be resumed before that.
    return this.context instanceof AudioContext ? this.context.resume() : Promise.resolve();
  }

  /**
   * The same instruments sounding into `context` instead of the speakers, to write a file.
   *
   * The recordings are shared rather than fetched and decoded again: a decoded buffer
   * belongs to no one context, and the file is meant to sound exactly as the panel does.
   */
  into(context: OfflineAudioContext): SampledEngine {
    return new SampledEngine(context, this.samples, this.fetching);
  }

  createVoice({
    program,
    plucked,
    muted,
    bridge,
    midi,
    at,
    length,
    peak,
    velocity,
    swell,
  }: VoiceRequest): SoundingTone | null {
    // The first of the sound's files this board has: a plucked string's own recordings, or
    // the same string bowed where it has none of those.
    const heard = recordingsOf({ program, plucked }).find(({ name }) => this.samples.has(name));
    const recorded = heard === undefined ? undefined : this.samples.get(heard.name);
    const chosen = recorded === undefined ? null : choose(recorded, midi, velocity);
    if (heard === undefined || chosen === null) return null;
    const { sample, rate, nominal } = chosen;
    const { instrument } = heard;

    const source = this.context.createBufferSource();
    source.buffer = sample.buffer;
    // The loop points are in the buffer's own time, so the shift leaves them where they are.
    source.playbackRate.value = rate;
    if (sample.loop !== null) {
      source.loop = true;
      source.loopStart = sample.loop.start;
      source.loopEnd = sample.loop.end;
    }

    // The recording has its own attack, so there is nothing to ramp up to: it starts at the
    // asked-for loudness, moves as the note swells, and is let go at the written end the way
    // its instrument lets go, falling away over a fraction of a second rather than stopping.
    const gain = this.context.createGain();
    const released = at + Math.max(length, 0);
    const end = released + instrument.letGo * TAIL_SPANS;
    const loud = peak * LEVEL;
    gain.gain.setValueAtTime(loud, at);
    swellInto(gain.gain, swell, at, released, LEVEL);
    if (swell.length === 0) gain.gain.setValueAtTime(loud, released);
    gain.gain.setTargetAtTime(0, released, instrument.letGo);

    // How hard it is played sets how bright it is, and a hairpin moves that too. A recording
    // made softly is already dark, so it is darkened only by how much softer the note is.
    const tone = this.context.createBiquadFilter();
    tone.type = "lowpass";
    tone.Q.value = 0;
    const hz = hertz(midi);
    const nyquist = this.context.sampleRate / 2;
    tone.frequency.setValueAtTime(brightness(hz, 1 + velocity - nominal, nyquist), at);
    for (const point of swell) {
      const when = Math.min(Math.max(point.at, at), released);
      tone.frequency.linearRampToValueAtTime(
        brightness(hz, 1 + point.velocity - nominal, nyquist),
        when,
      );
    }

    // A mute or the bridge colours what is heard and is no other recording (`sampler-manner`).
    const coloured = colouredBy(this.context, { program, muted, bridge }, hz);
    const chain: readonly AudioNode[] = [source, tone, ...coloured, gain];
    chain.forEach((node, at) => {
      const next = chain[at + 1];
      if (next !== undefined) node.connect(next);
    });
    gain.connect(this.room.seat(instrument.seat));
    source.start(at);
    source.stop(end);
    return {
      until: end,
      stop: (when: number): void => {
        try {
          source.stop(when);
        } finally {
          for (const node of chain) node.disconnect();
        }
      },
    };
  }

  /**
   * Whether {@link load} would have nothing left to do for these sounds.
   *
   * Asked before waiting rather than after: a score being edited hands the same instruments
   * over on every keystroke, and a caller that answered "loading" each time would hide the
   * play button behind a promise that was already kept.
   */
  ready(sounds: Iterable<Sound>): boolean {
    for (const sound of sounds) {
      for (const { name } of recordingsOf(sound)) if (!this.samples.has(name)) return false;
    }
    return true;
  }

  /**
   * Have the recordings for these sounds ready, and resolve when they are.
   *
   * Resolving does not mean every sound was found: one with no recording, or whose file
   * has not been fetched, is simply left out and its notes are sounded on the oscillator.
   * What it means is that waiting any longer would not improve the sound, which is what a
   * caller holding up a play button needs to know.
   */
  async load(sounds: Iterable<Sound>): Promise<void> {
    const jobs: Promise<void>[] = [];
    for (const recording of [...sounds].flatMap(recordingsOf)) {
      let job = this.fetching.get(recording.name);
      if (job === undefined) {
        job = this.read(recording);
        this.fetching.set(recording.name, job);
      }
      jobs.push(job);
    }
    await Promise.all(jobs);
  }

  /** Fetch and decode one file of recordings, or leave it unloaded and say nothing. */
  private async read(recording: Recording): Promise<void> {
    try {
      const recorded = await readRecorded(this.context, recording);
      if (recorded !== null) this.samples.set(recording.name, recorded);
    } catch {
      // No recordings for this instrument on this board. The oscillator answers instead,
      // and a board that has never run `pnpm sounds:fetch` takes this path for all of them.
    }
  }
}
